import { loadSiteFlowConditionFamily } from '../condition_families/siteFlowConditionFamily';
import {
  buildContributingConditionDisplayLine,
  type ContributingConditionStatus,
} from '../workplaceCondition/workplaceCondition';

export interface DiscouragedTermDefinition {
  term: string;
  preferred: string;
}

export interface DiscouragedTermMatch {
  term: string;
  preferred: string;
  matchedText: string;
}

export const DISCOURAGED_TERMS: DiscouragedTermDefinition[] = [
  { term: 'violation', preferred: 'recurring exposure' },
  { term: 'offender', preferred: 'reviewer hypothesis' },
  { term: 'unsafe worker', preferred: 'workplace condition' },
  { term: 'non-compliant worker', preferred: 'condition requiring review' },
  { term: 'worker failure', preferred: 'observed interaction' },
  { term: 'caught on camera', preferred: 'reviewed evidence' },
  { term: 'employee at fault', preferred: 'reviewer hypothesis' },
  { term: 'disciplinary incident', preferred: 'corrective action opportunity' },
  { term: 'careless worker', preferred: 'workplace condition' },
  { term: 'negligent worker', preferred: 'workplace condition' },
  { term: 'repeated worker behavior', preferred: 'recurring exposure' },
];

export const PREFERRED_TERMS = [
  'recurring exposure',
  'observed interaction',
  'workplace condition',
  'site-flow pattern',
  'condition requiring review',
  'reviewed evidence',
  'corrective action opportunity',
  'follow-up scan',
  'verified improvement',
  'monitoring continues',
  'reviewer hypothesis',
] as const;

export const WORKER_SUPPORT_NOTE_TEXT =
  'This report focuses on workplace conditions and corrective action. It is not intended to score individual workers.';

const GENERIC_WHY_THIS_MATTERS =
  'This matters because recurring workplace exposure should be reviewed as a site or workflow condition, not as individual worker behavior.';

const WHY_THIS_MATTERS_BY_CONDITION_TYPE: Record<string, string> = {
  vehicle_pedestrian_interaction:
    'This matters because repeated interaction between pedestrians and forklifts can indicate that normal site movement is creating avoidable exposure.',
  pedestrian_route_conflict:
    'This matters because route overlap can place normal pedestrian movement into recurring exposure areas.',
  forklift_route_conflict:
    'This matters because forklift routing patterns can shape recurring exposure where vehicles and pedestrians cross paths.',
  material_staging_issue:
    'This matters because staging placement can change how people and vehicles move through the zone and may create recurring exposure.',
  blocked_or_obstructed_walkway:
    'This matters because blocked walkways can push normal movement into less safe routes and recurring exposure.',
  congestion_bottleneck:
    'This matters because congestion can concentrate movement and increase recurring exposure in the same area.',
  high_traffic_overlap_zone:
    'This matters because repeated overlap between people, vehicles, or tasks can signal a site-flow condition worth review.',
  repeated_shortcut_path:
    'This matters because repeated shortcut paths may show the practical route is creating recurring exposure.',
  shift_transition_movement_conflict:
    'This matters because shift transitions can concentrate movement and increase recurring exposure at the same time.',
  poor_separation_people_vehicles:
    'This matters because limited separation between people and vehicles can make recurring exposure more likely during normal work.',
  unknown_needs_review:
    GENERIC_WHY_THIS_MATTERS,
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function findDiscouragedTerms(text: string): DiscouragedTermMatch[] {
  const source = String(text || '');
  if (!source.trim()) return [];

  const matches: DiscouragedTermMatch[] = [];
  const seen = new Set<string>();

  [...DISCOURAGED_TERMS]
    .sort((left, right) => right.term.length - left.term.length)
    .forEach((entry) => {
      const pattern = new RegExp(`\\b${escapeRegExp(entry.term)}\\b`, 'gi');
      const found = source.match(pattern);
      if (!found || seen.has(entry.term.toLowerCase())) return;
      seen.add(entry.term.toLowerCase());
      matches.push({
        term: entry.term,
        preferred: entry.preferred,
        matchedText: found[0] || entry.term,
      });
    });

  return matches;
}

export function suggestPreferredReplacements(text: string): string[] {
  return findDiscouragedTerms(text).map((match) => match.preferred);
}

export function buildDiscouragedLanguageWarning(text: string): string | null {
  const matches = findDiscouragedTerms(text);
  if (!matches.length) return null;
  const suggestions = matches.map((match) => `"${match.term}" -> "${match.preferred}"`).join('; ');
  return `This wording may sound punitive. Consider using condition-first language (${suggestions}).`;
}

export function buildDefaultWhyThisMattersText(conditionTypeId?: string | null): string {
  const key = String(conditionTypeId || '').trim();
  if (key && WHY_THIS_MATTERS_BY_CONDITION_TYPE[key]) {
    return WHY_THIS_MATTERS_BY_CONDITION_TYPE[key];
  }

  const pack = loadSiteFlowConditionFamily();
  const template = pack.operations_relevance_templates[key];
  if (template) {
    return template.startsWith('This matters because')
      ? template
      : `This matters because ${template.replace(/^This /, 'this ')}`;
  }

  return GENERIC_WHY_THIS_MATTERS;
}

export function defaultWorkerSupportNoteEnabled(
  context: { sharedOrExport?: boolean } = {},
): boolean {
  if (context.sharedOrExport === false) return false;
  return true;
}

export interface ReportLanguagePreviewInput {
  conditionSummary?: string | null;
  whyThisMattersText?: string | null;
  likelyContributingCondition?: string | null;
  contributingConditionStatus?: ContributingConditionStatus | string | null;
  workerSupportNoteEnabled?: boolean | null;
}

export function buildReportLanguagePreview(input: ReportLanguagePreviewInput): {
  sections: Array<{ label: string; text: string }>;
  combinedText: string;
  discouragedMatches: DiscouragedTermMatch[];
  warning: string | null;
} {
  const sections: Array<{ label: string; text: string }> = [];
  const conditionSummary = String(input.conditionSummary || '').trim();
  const whyThisMattersText = String(input.whyThisMattersText || '').trim();
  const likelyContributingCondition = String(input.likelyContributingCondition || '').trim();
  const contributingDisplayLine = buildContributingConditionDisplayLine(
    likelyContributingCondition,
    input.contributingConditionStatus,
  );
  const workerSupportEnabled = input.workerSupportNoteEnabled !== false;

  if (conditionSummary) {
    sections.push({ label: 'Workplace condition observed', text: conditionSummary });
  }
  if (whyThisMattersText) {
    sections.push({ label: 'Why this matters', text: whyThisMattersText });
  }
  if (contributingDisplayLine) {
    sections.push({
      label: 'Likely contributing condition',
      text: contributingDisplayLine,
    });
  }
  if (workerSupportEnabled) {
    sections.push({ label: 'Worker-support note', text: WORKER_SUPPORT_NOTE_TEXT });
  }

  const combinedText = sections.map((section) => `${section.label}: ${section.text}`).join('\n\n');
  const discouragedMatches = findDiscouragedTerms(
    [conditionSummary, whyThisMattersText, likelyContributingCondition].filter(Boolean).join('\n'),
  );

  return {
    sections,
    combinedText,
    discouragedMatches,
    warning: buildDiscouragedLanguageWarning(
      [conditionSummary, whyThisMattersText, likelyContributingCondition].filter(Boolean).join('\n'),
    ),
  };
}
